import React, { useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { QUOTA_EXCEEDED_EVENT, type QuotaExceededDetail } from '@/auth/apiClient';

/**
 * Shows what the user hit and what they can do about it, whenever the API answers 402.
 *
 * <p>Mounted once at the app shell. The alternative — handling 402 per screen — would mean
 * touching every service, most of which currently stringify the whole response body into an
 * `Error` message and discard the status code, so an over-quota user would see a raw JSON blob
 * in a red paragraph and no idea that upgrading is the fix.</p>
 *
 * <p>Deliberately not a modal: quota refusals happen mid-workflow (enabling a scanner, starting
 * a backtest) and blocking the whole screen to say "that didn't run" is worse than a dismissible
 * banner the user can read while carrying on.</p>
 */
export const UpgradePrompt: React.FC = () => {
  const [detail, setDetail] = useState<QuotaExceededDetail | null>(null);

  useEffect(() => {
    const onQuota = (e: Event) => {
      const custom = e as CustomEvent<QuotaExceededDetail>;
      if (custom.detail) setDetail(custom.detail);
    };
    window.addEventListener(QUOTA_EXCEEDED_EVENT, onQuota);
    return () => window.removeEventListener(QUOTA_EXCEEDED_EVENT, onQuota);
  }, []);

  if (!detail) return null;

  return (
    <div
      role="status"
      className="fixed bottom-4 right-4 z-50 max-w-sm rounded-lg border border-amber-500/40 bg-background shadow-lg"
    >
      <div className="flex items-start gap-2 p-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
        <div className="min-w-0 space-y-1">
          <div className="text-sm font-semibold">Plan limit reached</div>
          <div className="text-xs text-muted-foreground">{detail.error}</div>
          {detail.upgradeHint && (
            <div className="text-xs text-muted-foreground">{detail.upgradeHint}</div>
          )}
        </div>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => setDetail(null)}
          className="ml-auto rounded p-1 text-muted-foreground hover:bg-accent"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

export default UpgradePrompt;
